import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import CalculateIcon from "@mui/icons-material/Calculate";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import CustomTypography from "../../globalStyles/fonts";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { demandProfileService } from "../../utils/services/demandProfileService";
import buildQuery from "../../utils/query-builder";
import chartIcon from "../../assets/chart-icon.png";
import starIcon from "../../assets/star-icon.png";
import { profileAssumptionService } from "../../utils/services/profileAssumptionService";
import { staticAssumptionService } from "../../utils/services/staticAssumptionService";

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  fontFamily: "Poppins",
  color: "var(--text-tertiary)",
}));

const FieldLabel = styled(CustomTypography)(({ theme }) => ({
  textAlign: "left",
  fontWeight: 400,
  display: "block",
  marginBottom: theme.spacing(0.5),
}));

const CalculateButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#E53935",
  color: "white",
  fontWeight: 600,
  fontSize: "16px",
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#C62828",
  },
  fontFamily: "Poppins",
}));

const ResetButton = styled(Button)(({ theme }) => ({
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: "16px",
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  border: "1px solid var(--border-color)",
  fontFamily: "Poppins",
  "&:hover": {
    backgroundColor: "#F3F4F6",
  },
}));

const KPICard = styled(Card)(({ theme }) => ({
  border: "none",
  borderRadius: "8px",
  marginBottom: theme.spacing(1.5),
  backgroundColor: "#F5F7FA",
  boxShadow: "none",
  "& .MuiCardContent-root": {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

const KPILabel = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "Poppins",
  color: "var(--text-secondary)",
}));

const KPIValue = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 700,
  fontFamily: "Poppins",
  color: "var(--text-tertiary)",
}));

const BenefitCard = styled(Card)(({ theme }) => ({
  border: "2px solid #10B981",
  borderRadius: "8px",
  backgroundColor: "#ECFDF5",
  "& .MuiCardContent-root": {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const ReadyToCalculateBox = styled(Card)(({ theme }) => ({
  border: "none",
  borderRadius: "16px",
  padding: theme.spacing(4),
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100%",
  height: "100%",
  background: "linear-gradient(135deg, #F0F4FF 0%, #F8FAFF 100%)",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(91, 127, 255, 0.08)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50px",
    right: "-50px",
    width: "200px",
    height: "200px",
    background: "rgba(91, 127, 255, 0.05)",
    borderRadius: "50%",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-80px",
    left: "-80px",
    width: "250px",
    height: "250px",
    background: "rgba(16, 185, 129, 0.03)",
    borderRadius: "50%",
  },
}));

const DecorativeStarIcon = styled("img")(({ theme }) => ({
  position: "absolute",
  opacity: 0.4,
  width: "60px",
  height: "60px",

  "&.star-top": {
    top: "20px",
    right: "20px",
  },

  "&.star-bottom": {
    bottom: "30px",
    left: "20px",
    width: "40px",
    height: "40px",
  },
}));

// ===== NEW STYLED COMPONENTS FOR RESULTS PANEL =====
const ResultsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  maxHeight: "calc(100vh - 120px)",
  [theme.breakpoints.down("sm")]: {
    maxHeight: "none",
  },
}));

const ResultsHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2.5),
}));

const StatusBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: "13px",
  color: "#10B981",
  backgroundColor: "rgba(16, 185, 129, 0.1)",
  padding: theme.spacing(0.5, 1),
  borderRadius: "20px",
  fontWeight: 500,
}));

const PulsingDot = styled(Box)(({ theme }) => ({
  width: "6px",
  height: "6px",
  backgroundColor: "#10B981",
  borderRadius: "50%",
  animation: "pulse 2s infinite",
  "@keyframes pulse": {
    "0%": {
      opacity: 0.5,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 1,
      transform: "scale(1.2)",
    },
    "100%": {
      opacity: 0.5,
      transform: "scale(1)",
    },
  },
}));

const HeroCard = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2.5),
  padding: theme.spacing(2),
  backgroundColor: "var(--bg-paper)",
  borderRadius: "12px",
  border: "1px solid var(--border-color)",
}));

const HeroLabel = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 500,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: theme.spacing(0.75),
}));

const HeroValue = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  fontWeight: 700,
  color: "#10B981",
  fontFamily: "'Outfit', sans-serif",
  lineHeight: 1,
  marginBottom: theme.spacing(0.5),
}));

const HeroSubtext = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: "#10B981",
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "220px",
  marginBottom: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const ChartOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  textAlign: "center",
  zIndex: 10,
  pointerEvents: "none",
}));

const ChartValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#1F2937",
  fontFamily: "'Outfit', sans-serif",
}));

const ChartLabel = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginTop: theme.spacing(0.5),
}));

const MetricsGrid = styled(Grid)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const MetricBoxWide = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "var(--bg-paper)",
  borderRadius: "12px",
  border: "1px solid var(--border-color)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const MetricBox = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1.25),
  backgroundColor: "#F9FAFB",
  borderRadius: "8px",
  border: "1px solid var(--border-color)",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  gridColumn: "1 / -1",
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 600,
  color: "var(--text-secondary)",
  fontFamily: "'Inter', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.3px",
}));

const MetricLabelWide = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--text-tertiary)",
  fontFamily: "'Poppins', sans-serif",
  textTransform: "none",
  letterSpacing: "0px",
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 700,
  color: "#1F2937",
  fontFamily: "'Outfit', sans-serif",
  lineHeight: 1.2,
}));

const MetricComparison = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: "14px",
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 600,
}));

const MetricComparisonWide = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  fontSize: "16px",
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  padding: theme.spacing(1),
  backgroundColor: "#F0FDFB",
  borderRadius: "8px",
}));

const OldValue = styled(Typography)(({ theme }) => ({
  textDecoration: "line-through",
  color: "#9CA3AF",
  fontSize: "13px",
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 500,
}));

const OldValueWide = styled(Typography)(({ theme }) => ({
  color: "var(--text-secondary)",
  fontSize: "15px",
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 500,
  textDecoration: "line-through",
}));

const Arrow = styled(Typography)(({ theme }) => ({
  color: "#D1D5DB",
  fontSize: "12px",
  flex: 1,
}));

const NewValue = styled(Typography)(({ theme }) => ({
  color: "#10B981",
  fontWeight: 700,
  fontSize: "13px",
  fontFamily: "'Outfit', sans-serif",
  textAlign: "right",
}));

const NewValueWide = styled(Typography)(({ theme }) => ({
  color: "#10B981",
  fontWeight: 700,
  fontSize: "15px",
  fontFamily: "'Poppins', sans-serif",
  textAlign: "right",
}));

const KPISummaryBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "var(--bg-paper)",
  border: "1px solid var(--border-color)",
  borderRadius: "8px",
  marginBottom: theme.spacing(1.5),
}));

const KPISummaryRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const KPISummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--text-secondary)",
}));

const KPISummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "var(--text-tertiary)",
  fontFamily: "'Outfit', sans-serif",
}));

const initialData = {
  category: "",
  annualCogs: "",
  annualRevenue: "",
  grossMargin: "",
  inScope: "",
  paymentTerms: "",
  costOfCapital: 12,
  safetyStockWeeks: "",
  markdownLossRate: "",
  safetyReduction: "",
  markdownReduction: "",
};
const DemandCalculation = () => {
  const [formData, setFormData] = useState(initialData);
  const [calculated, setCalculated] = useState(false);
  const [kpiOutput, setKpiOutput] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const handleInputChange = (field, value) => {
    if (field === "category") {
      fetchProfileAssumption(value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  useEffect(() => {
    async function fetchStaticAssumptions() {
      let staticAssumptions = await staticAssumptionService.fetch(
        "",
        `key[$in][]=safety_stock_reduction_rl_percentage&key[$in][]=markdown_reduction_rl_percentage`,
      );
      setFormData((prev) => ({
        ...prev,
        safetyReduction:
          staticAssumptions.data.find(
            (sa) => sa.key === "safety_stock_reduction_rl_percentage",
          )?.value || "",
        markdownReduction:
          staticAssumptions.data.find(
            (sa) => sa.key === "markdown_reduction_rl_percentage",
          )?.value || "",
      }));
    }
    fetchStaticAssumptions();
  }, []);

  const fetchProfileAssumption = async (category) => {
    try {
      let assumption = await profileAssumptionService.fetch(
        "",
        `profile=${category._id}`,
      );
      setFormData((prev) => ({
        ...prev,
        safetyStockWeeks: assumption.data[0]?.safetyStockWeeks || "",
        markdownLossRate: assumption.data[0]?.lossRate || "",
        category: category,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleCalculate = async () => {
    try {
      console.log(formData);
      if (!formData.category._id) {
        toast.error("Category / demand curve is required");
        return;
      }

      let response = await demandProfileService.calculate(formData);
      setKpiOutput({
        dioCurrentDays: response.dioCurrentDays.toLocaleString(),
        dioRlDays: response.dioRlDays.toLocaleString(),
        inventoryTurnsCurrent: response.inventoryTurnsCurrent.toFixed(2),
        inventoryTurnsRl: response.inventoryTurnsRl.toFixed(2),
        cashUnlocked: response.cashUnlocked.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        annualCocSavings: response.annualCocSavings.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        annualMarkdownSavings: response.annualMarkdownSavings.toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ),
        totalAnnualBenefit: response.totalAnnualBenefit.toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ),
        baselineSafetyStock: response.baselineSafetyStock.toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ),
        reducedSafetyStock: response.reducedSafetyStock.toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ),
        safetyStockCashReleased:
          response.safetyStockCashReleased.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        grossProfit: response.grossProfit.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        roi: response.roi.toFixed(2),
      });

      setCalculated(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = () => {
    setFormData(initialData);
    setCalculated(false);
    setKpiOutput(null);
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }
  };

  // Initialize chart when kpiOutput changes
  useEffect(() => {
    if (calculated && chartRef.current && kpiOutput) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext("2d");
      const cashValue = kpiOutput.cashUnlocked.replace(/[$,]/g, "");
      const totalValue = parseFloat(cashValue) * 1.5; // Estimate total for visualization

      chartInstance.current = new ChartJS(ctx, {
        type: "doughnut",
        data: {
          labels: ["Cash Unlocked", "Remaining Inventory"],
          datasets: [
            {
              data: [parseFloat(cashValue), totalValue - parseFloat(cashValue)],
              backgroundColor: ["#10B981", "#D1D5DB"],
              borderColor: "#FFFFFF",
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "70%",
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (context) {
                  return "$" + context.parsed.toFixed(0);
                },
              },
            },
          },
        },
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [calculated, kpiOutput]);

  return (
    <StyledContainer maxWidth="lg">
      {/* Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              background: "linear-gradient(135deg, #E53935 0%, #C62828 100%)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 800,
              fontSize: "1rem",
              boxShadow: "0 4px 15px rgba(229, 57, 53, 0.3)",
            }}
          >
            RL
          </Box>
          <Typography
            sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1F2937" }}
          >
            Redefined Logistics{" "}
            <span
              style={{
                fontSize: "0.75rem",
                background: "rgba(229, 57, 53, 0.1)",
                color: "#E53935",
                padding: "0.2rem 0.6rem",
                borderRadius: "20px",
                fontWeight: 500,
                marginLeft: "0.75rem",
              }}
            >
              ROI ENGINE
            </span>
          </Typography>
        </Box>
        <Typography sx={{ color: "var(--text-secondary)", fontSize: "0.95rem" }}>
          Calculate Your Inventory Optimization and Cashflow Benefits
        </Typography>
      </Box> */}

      {/* Main Grid */}
      <Grid container spacing={3}>
        {/* Left Section - Inputs */}
        <Grid item xs={12} md={6}>
          <StyledPaper
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <SectionTitle>Client Inputs</SectionTitle>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {/* Category / Demand Curve */}
              <Grid item xs={12}>
                <FieldLabel fontSize={14}>Category / demand curve</FieldLabel>
                <AutocompleteWithPagination
                  hideLabel
                  value={formData.category}
                  onDataChange={(value) => handleInputChange("category", value)}
                  apiEndPoint={(limit, skip, search) =>
                    demandProfileService.fetch(
                      search,
                      buildQuery({ limit, skip }),
                    )
                  }
                  optionKey={"item"}
                  width={"100%"}
                />
              </Grid>

              {/* Annual COGS & Revenue */}
              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>Annual COGS ($)</FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="number"
                  size="small"
                  value={formData.annualCogs}
                  onChange={(e) =>
                    handleInputChange(
                      "annualCogs",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>Annual Revenue ($)</FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="number"
                  size="small"
                  value={formData.annualRevenue}
                  onChange={(e) =>
                    handleInputChange(
                      "annualRevenue",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  fullWidth
                />
              </Grid>

              {/* Gross Margin & In-Scope */}
              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>Gross Margin (%)</FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="number"
                  size="small"
                  value={formData.grossMargin}
                  onChange={(e) =>
                    handleInputChange(
                      "grossMargin",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>In-Scope Inventory (%)</FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="number"
                  size="small"
                  value={formData.inScope}
                  onChange={(e) =>
                    handleInputChange(
                      "inScope",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  fullWidth
                />
              </Grid>

              {/* Supplier Payment Terms */}
              <Grid item xs={12}>
                <FieldLabel fontSize={14}>
                  Supplier Payment Terms (Days)
                </FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="number"
                  size="small"
                  value={formData.paymentTerms}
                  onChange={(e) =>
                    handleInputChange(
                      "paymentTerms",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  fullWidth
                />
              </Grid>

              {/* Cost of Capital */}
              <Grid item xs={12}>
                <FieldLabel fontSize={14}>Cost of Capital (%)</FieldLabel>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.costOfCapital}
                    onChange={(e) =>
                      handleInputChange(
                        "costOfCapital",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    style={{
                      flex: 1,
                      height: "4px",
                      borderRadius: "2px",
                      background: "#E5E7EB",
                      outline: "none",
                      WebkitAppearance: "none",
                      appearance: "none",
                    }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    value={formData.costOfCapital}
                    onChange={(e) =>
                      handleInputChange(
                        "costOfCapital",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    sx={{
                      width: "60px",
                      backgroundColor: "var(--bg-paper)",
                      "& input": { textAlign: "center" },
                    }}
                  />
                </Box>
                <style>
                  {`
                    input[type="range"]::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      appearance: none;
                      width: 18px;
                      height: 18px;
                      background: #E53935;
                      border-radius: 50%;
                      cursor: pointer;
                      box-shadow: 0 0 8px rgba(229, 57, 53, 0.4);
                    }
                    input[type="range"]::-moz-range-thumb {
                      width: 18px;
                      height: 18px;
                      background: #E53935;
                      border-radius: 50%;
                      cursor: pointer;
                      border: none;
                      box-shadow: 0 0 8px rgba(229, 57, 53, 0.4);
                    }
                  `}
                </style>
              </Grid>
            </Grid>

            {/* Optional Section */}
            <SectionTitle sx={{ mt: 3 }}>Optional Overrides</SectionTitle>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>
                  Current Safety Stock (Weeks){" "}
                  <span style={{ color: "var(--text-secondary)" }}>opt</span>
                </FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="text"
                  size="small"
                  placeholder="Optional"
                  value={formData.safetyStockWeeks}
                  onChange={(e) =>
                    handleInputChange("safetyStockWeeks", e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>
                  Markdown Loss Rate{" "}
                  <span style={{ color: "var(--text-secondary)" }}>opt</span>
                </FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="text"
                  size="small"
                  placeholder="Optional"
                  value={formData.markdownLossRate}
                  onChange={(e) =>
                    handleInputChange("markdownLossRate", e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>
                  Safety Reduction{" "}
                  <span style={{ color: "var(--text-secondary)" }}>opt</span>
                </FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="text"
                  size="small"
                  placeholder="Optional"
                  value={formData.safetyReduction}
                  onChange={(e) =>
                    handleInputChange("safetyReduction", e.target.value)
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FieldLabel fontSize={14}>
                  Markdown Reduction{" "}
                  <span style={{ color: "var(--text-secondary)" }}>opt</span>
                </FieldLabel>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="text"
                  size="small"
                  placeholder="Optional"
                  value={formData.markdownReduction}
                  onChange={(e) =>
                    handleInputChange("markdownReduction", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <CalculateButton
                fullWidth
                startIcon={<CalculateIcon />}
                onClick={handleCalculate}
              >
                Calculate ROI
              </CalculateButton>
              <ResetButton
                fullWidth
                startIcon={<RotateLeftIcon />}
                onClick={handleReset}
              >
                Reset
              </ResetButton>
            </Stack>
          </StyledPaper>
        </Grid>

        {/* Right Section - Results */}
        <Grid item xs={12} md={6}>
          {!calculated ? (
            <ReadyToCalculateBox>
              <DecorativeStarIcon
                src={starIcon}
                alt="star"
                className="star-top"
              />
              <DecorativeStarIcon
                src={starIcon}
                alt="star"
                className="star-bottom"
              />

              <Box sx={{ mb: 3, position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    width: "100px",
                    height: "100px",
                    background:
                      "linear-gradient(135deg, #5B7FFF 0%, #7C3AED 100%)",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(91, 127, 255, 0.3)",
                  }}
                >
                  <img
                    src={chartIcon}
                    alt="chart"
                    style={{
                      width: "56px",
                      height: "56px",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  mb: 1,
                  position: "relative",
                  zIndex: 1,
                  color: "#1F2937",
                }}
              >
                Ready to Calculate
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  mb: 4,
                  position: "relative",
                  zIndex: 1,
                  color: "var(--text-secondary)",
                  maxWidth: "280px",
                  lineHeight: "1.6",
                }}
              >
                Fill in your business parameters and click{" "}
                <span style={{ color: "#E53935", fontWeight: 600 }}>
                  "Calculate"
                </span>{" "}
                to see your KPI results
              </Typography>

              <Stack
                spacing={2.5}
                sx={{
                  width: "100%",
                  position: "relative",
                  zIndex: 1,
                  maxWidth: "300px",
                }}
              >
                {[
                  {
                    number: 1,
                    text: "Enter client inputs",
                    subtext: "Category, COGS, revenue, etc.",
                  },
                  {
                    number: 2,
                    text: "Add optional overrides",
                    subtext: "Customize assumptions if needed",
                  },
                  {
                    number: 3,
                    text: "Calculate & view results",
                    subtext: "See your ROI and savings instantly",
                  },
                ].map((step) => (
                  <Box
                    key={step.number}
                    sx={{
                      display: "flex",
                      gap: 2.5,
                      alignItems: "flex-start",
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.8)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        boxShadow: "0 4px 12px rgba(91, 127, 255, 0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        background:
                          step.number === 3
                            ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                            : "linear-gradient(135deg, #5B7FFF 0%, #7C3AED 100%)",
                        color: "white",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        minWidth: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "16px",
                        flexShrink: 0,
                        boxShadow:
                          step.number === 3
                            ? "0 4px 12px rgba(16, 185, 129, 0.3)"
                            : "0 4px 12px rgba(91, 127, 255, 0.3)",
                      }}
                    >
                      {step.number}
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <CustomTypography
                        fontSize="14px"
                        fontWeight={600}
                        sx={{ mb: 0.5, color: "#1F2937" }}
                      >
                        {step.text}
                      </CustomTypography>
                      <CustomTypography
                        fontSize="12px"
                        sx={{ color: "#9CA3AF" }}
                      >
                        {step.subtext}
                      </CustomTypography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </ReadyToCalculateBox>
          ) : (
            <ResultsPaper>
              {/* Header with Status */}
              <ResultsHeader>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 700, color: "#1F2937" }}
                >
                  Projection Analysis
                </Typography>
                <StatusBadge>
                  <PulsingDot />
                  Live Estimate
                </StatusBadge>
              </ResultsHeader>

              {/* Hero Card - Total Annual Benefit */}
              <HeroCard>
                <HeroLabel>Total Annual Benefit</HeroLabel>
                <HeroValue>{kpiOutput?.totalAnnualBenefit}</HeroValue>
                <HeroSubtext>▲ {kpiOutput?.roi}% ROI project yield</HeroSubtext>
              </HeroCard>

              {/* Chart */}
              <ChartContainer>
                <canvas ref={chartRef}></canvas>
                <ChartOverlay>
                  <ChartValue>{kpiOutput?.cashUnlocked}</ChartValue>
                  <ChartLabel>Cash Unlocked</ChartLabel>
                </ChartOverlay>
              </ChartContainer>

              {/* Metrics Grid */}
              <MetricsGrid>
                {/* DIO Improvement */}
                <MetricBoxWide>
                  <MetricLabelWide>DIO Improvement</MetricLabelWide>
                  <MetricComparisonWide>
                    <OldValueWide>
                      {kpiOutput?.dioCurrentDays} days
                    </OldValueWide>
                    <Arrow>→</Arrow>
                    <NewValueWide>{kpiOutput?.dioRlDays} days</NewValueWide>
                  </MetricComparisonWide>
                </MetricBoxWide>

                {/* Inventory Turns */}
                <MetricBoxWide>
                  <MetricLabelWide>Inventory Turns</MetricLabelWide>
                  <MetricComparisonWide>
                    <OldValueWide>
                      {kpiOutput?.inventoryTurnsCurrent}x
                    </OldValueWide>
                    <Arrow>→</Arrow>
                    <NewValueWide>{kpiOutput?.inventoryTurnsRl}x</NewValueWide>
                  </MetricComparisonWide>
                </MetricBoxWide>

                {/* COC Savings */}
                <MetricBox>
                  <MetricLabel>COC Savings</MetricLabel>
                  <MetricValue>{kpiOutput?.annualCocSavings}</MetricValue>
                </MetricBox>

                {/* Markdown Savings */}
                <MetricBox>
                  <MetricLabel>Markdown Savings</MetricLabel>
                  <MetricValue>{kpiOutput?.annualMarkdownSavings}</MetricValue>
                </MetricBox>
              </MetricsGrid>
            </ResultsPaper>
          )}
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default DemandCalculation;
